/**
 * RelaxMenu — popover with a slider to relax selected bones toward rest pose.
 *
 * Shown when pressing X in pose mode with bones selected. The slider
 * controls the relax factor (0% = no change, 100% = fully at rest).
 * Changes are applied live with undo support.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "../app/store.ts";
import { FloatingCommandMenu } from "./FloatingCommandMenu.tsx";
import { useDeferredUndo } from "./hooks/useDeferredUndo.ts";
import { useOperatorMenu } from "./hooks/useOperatorMenu.ts";
import { Slider } from "./Slider.tsx";
import { SegmentedControl } from "./shared.tsx";
import {
  COLOR_TEXT_DIM,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_SMALL,
} from "./tokens.ts";

const FALLOFF_OPTIONS = [
  { label: "0", value: 0 },
  { label: "1", value: 1 },
  { label: "2", value: 2 },
] as const;

export function RelaxMenu() {
  const menu = useOperatorMenu("relax");
  const undo = useDeferredUndo();
  const falloff = useStore((s) => s.relaxFalloff);
  const setRelaxFalloff = useStore((s) => s.setRelaxFalloff);

  const [factor, setFactor] = useState(0);
  // Captured original poses when popover opens — relax always slerps from these
  const originalPosesRef = useRef<Map<
    string,
    [number, number, number, number]
  > | null>(null);

  const close = useCallback(() => {
    setFactor(0);
    originalPosesRef.current = null;
    undo.reset();
    menu.close();
  }, [menu, undo]);

  const applyFactor = useCallback(
    (newFactor: number) => {
      setFactor(newFactor);
      undo.pushOnce();
      const originals = originalPosesRef.current;
      if (originals) {
        useStore.getState().relaxBonesFrom(originals, newFactor / 100);
      }
    },
    [undo],
  );

  // When falloff changes, re-apply the current factor so the result updates live
  const handleFalloffChange = useCallback(
    (newFalloff: number) => {
      setRelaxFalloff(newFalloff);
      // Re-apply current factor with the new falloff (store reads relaxFalloff internally)
      const originals = originalPosesRef.current;
      if (originals && factor > 0) {
        undo.pushOnce();
        useStore.getState().relaxBonesFrom(originals, factor / 100);
      }
    },
    [factor, setRelaxFalloff, undo],
  );

  // Capture original poses when popover opens
  useEffect(() => {
    if (menu.open) {
      setFactor(0);
      undo.reset();
      const s = useStore.getState();
      const pm = s.poseMode;
      if (pm?.objectId) {
        const current = s.mannequinBonePoses.get(pm.objectId);
        originalPosesRef.current = current ? new Map(current) : new Map();
      }
    }
  }, [menu.open, undo]);

  return (
    <FloatingCommandMenu
      open={menu.open}
      onClose={close}
      title="Relax Bones"
      width={240}
      anchorPoint={menu.anchorPoint}
    >
      <div
        style={{
          padding: "4px 6px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_DIM,
            width: 40,
            flexShrink: 0,
          }}
        >
          Factor
        </span>
        <Slider
          value={factor}
          min={0}
          max={100}
          onChange={applyFactor}
          dimChromeOnDrag
        />
        <span
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_SECONDARY,
            width: 36,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
            flexShrink: 0,
          }}
        >
          {factor}%
        </span>
      </div>
      <div
        style={{
          padding: "4px 6px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_DIM,
            width: 40,
            flexShrink: 0,
          }}
        >
          Falloff
        </span>
        <SegmentedControl
          options={
            FALLOFF_OPTIONS as unknown as { label: string; value: number }[]
          }
          value={falloff}
          onChange={handleFalloffChange}
        />
      </div>
    </FloatingCommandMenu>
  );
}
